import type { EditorState, Transaction } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';

import type { AlignMenuController } from './align-plugin.types';
import type { EditorButton, EditorMode, RtPMPlugin } from '../../editor/types';
import type { AlignValue } from '../../extensions/align';
import { AlignExtension } from '../../extensions/align';

class AlignEditorButton implements EditorButton {
  public readonly isDropdown = true;
  private hasAlignCenter = AlignExtension.has('center');
  private hasAlignRight = AlignExtension.has('right');

  constructor(
    private editorView: EditorView,
    private menuController: AlignMenuController,
  ) {}

  public get icon() {
    switch (this.getActiveAlign()) {
      case 'right':
        return 'format-align-right';
      case 'center':
        return 'format-align-center';
      default:
        return 'format-align-left';
    }
  }

  public get tooltipText() {
    return this.menuController.isOpen() ? '' : 'wysiwyg.align';
  }

  public isActive(state: EditorState) {
    return this.menuController.isOpen();
  }

  public run(state: EditorState, dispatch: (tr: Transaction) => void, origin: HTMLElement) {
    const { backdropClick$ } = this.menuController.open(origin.getBoundingClientRect(), {
      getActive: () => this.getActiveAlign(state),
      select: align => {
        this.setAlign(align, state, dispatch);
        this.menuController.close();
        this.editorView.focus();
      },
    });
    backdropClick$.subscribe(() => {
      this.menuController.close();
      this.editorView.focus();
    });
    this.editorView.focus();
  }

  private getActiveAlign(state: EditorState = this.editorView.state): AlignValue {
    if (this.hasAlignRight(state)) {
      return 'right';
    } else if (this.hasAlignCenter(state)) {
      return 'center';
    } else {
      return 'left';
    }
  }

  private setAlign(val: AlignValue, state: EditorState, dispatch: (tr: Transaction) => void) {
    const cmd = AlignExtension.apply(val);
    cmd(state, dispatch);
  }
}

export class AlignPlugin implements RtPMPlugin {
  public buttons: { toggleAlign: EditorButton };
  private editorView: EditorView;

  constructor(private menuController: AlignMenuController) {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public beforeInit(mode: EditorMode) {}

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
    this.buttons = {
      toggleAlign: new AlignEditorButton(editorView, this.menuController),
    };
  }
}
